import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, Send } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';

const categories = [
  { id: 'architecture', label: '건축제휴' },
  { id: 'business', label: '업무제휴' }
];

export default function Partners() {
  const [activeCategory, setActiveCategory] = useState('architecture');
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { language } = useLanguage();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    title: '',
    message: ''
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    let formattedValue = '';
    
    if (value.length < 4) {
      formattedValue = value;
    } else if (value.length < 7 && value.startsWith('02')) {
      formattedValue = `${value.slice(0, 2)}-${value.slice(2)}`;
    } else if (value.length < 8) {
      formattedValue = `${value.slice(0, 3)}-${value.slice(3)}`;
    } else if (value.length < 10 && value.startsWith('02')) {
      formattedValue = `${value.slice(0, 2)}-${value.slice(2, 5)}-${value.slice(5)}`;
    } else if (value.length < 11 && value.startsWith('02')) {
      formattedValue = `${value.slice(0, 2)}-${value.slice(2, 6)}-${value.slice(6)}`;
    } else if (value.length < 11) {
      formattedValue = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
    } else if (value.length < 12) {
      formattedValue = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`;
    } else {
      formattedValue = `${value.slice(0, 4)}-${value.slice(4, 8)}-${value.slice(8, 12)}`;
    }
    
    setFormData({...formData, phone: formattedValue});
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const phoneRegex = /^(0[0-9]{1,3})-?[0-9]{3,4}-?[0-9]{4}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert(language === 'ko' ? "올바른 전화번호 형식을 입력해주세요. (예: 010-1234-5678)" : "Please enter a valid phone number format. (e.g. 010-1234-5678)");
      return;
    }

    setIsSubmitting(true);
    
    const { error } = await supabase
      .from('partnerships')
      .insert([
        {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: `${formData.title}\n\n${formData.message}`,
          type: activeCategory === 'architecture' ? 'architectural' : 'business',
          status: 'new'
        }
      ]);

    setIsSubmitting(false);

    if (error) {
      console.error('Submission error:', error);
      alert(language === 'ko' ? "문의 접수 중 오류가 발생했습니다. 다시 시도해주세요." : "An error occurred while submitting your inquiry. Please try again.");
    } else {
      alert(language === 'ko' ? "문의가 접수되었습니다." : "Your inquiry has been submitted.");
      setFormData({ name: '', phone: '', email: '', title: '', message: '' });
      setAgreed(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <SEO 
        title={language === 'ko' ? '파트너스 | 빅플래너파트너스' : 'Partners | BIGPLANNER PARTNERS'}
        description={language === 'ko' ? "빅플래너파트너스와 함께할 건축 및 업무 파트너를 모집합니다. 제휴 문의를 남겨주세요." : "We are looking for architecture and business partners to join BIGPLANNER PARTNERS. Please leave a partnership inquiry."}
        url="https://bigplanner.co.kr/partners"
        image="https://injrbniytgtubemniaps.supabase.co/storage/v1/object/public/bigplanner/logo.png"
      />
      <Helmet>
        <meta name="keywords" content="빅플래너파트너스, 파트너스, 건축제휴, 업무제휴, 프롭테크, BIGPLANNER PARTNERS, Partners, Architecture Partnership, Business Partnership, Proptech" />
        <link rel="canonical" href="https://bigplanner.co.kr/partners" />
        <script type="application/ld+json">
          {`
            [
              {
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "${language === 'ko' ? '파트너스 | 빅플래너파트너스' : 'Partners | BIGPLANNER PARTNERS'}",
                "description": "${language === 'ko' ? '빅플래너파트너스와 함께할 건축 및 업무 파트너를 모집합니다.' : 'We are looking for architecture and business partners to join BIGPLANNER PARTNERS.'}",
                "url": "https://bigplanner.co.kr/partners"
              },
              {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "${language === 'ko' ? '홈' : 'Home'}",
                    "item": "https://bigplanner.co.kr/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "${language === 'ko' ? '파트너스' : 'Partners'}",
                    "item": "https://bigplanner.co.kr/partners"
                  }
                ]
              }
            ]
          `}
        </script>
      </Helmet>
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Partners</h1>
            <p className="text-lg text-gray-500">{language === 'ko' ? '파트너스' : 'Partners'}</p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm p-2 flex justify-center space-x-2 md:space-x-4 border border-gray-100">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex-1 px-4 py-3 md:px-8 md:py-4 rounded-xl font-bold text-sm transition-all ${
                activeCategory === cat.id
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Form Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white p-6 md:p-12 rounded-2xl md:rounded-3xl border border-gray-100 shadow-sm"
          >
            <div className="mb-8 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                {activeCategory === 'architecture' ? '건축제휴 문의' : '업무제휴 문의'}
              </h3>
              <p className="text-gray-500">문의를 남겨주시면 신속하게 답변드리겠습니다.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">성함 / 회사명</label>
                  <input 
                    type="text" 
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                    placeholder="성함 또는 회사명을 입력해주세요"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">연락처</label>
                  <input 
                    type="tel" 
                    required
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                    placeholder="연락처를 입력해주세요"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">이메일</label>
                <input 
                  type="email" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                  placeholder="이메일 주소를 입력해주세요"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">제목</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                  placeholder="제목을 입력해주세요"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">내용</label>
                <textarea 
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="문의 내용을 상세히 입력해주세요"
                ></textarea>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <div className="flex items-start mb-6">
                  <button 
                    type="button"
                    onClick={() => setAgreed(!agreed)}
                    className={`mt-1 mr-3 flex-shrink-0 w-5 h-5 rounded border transition-colors flex items-center justify-center ${
                      agreed ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
                    }`}
                  >
                    {agreed && <CheckCircle2 size={14} className="text-white" />}
                  </button>
                  <div className="text-sm">
                    <p className="text-gray-700 font-medium mb-1">개인정보 수집 및 이용 동의 (필수)</p>
                    <p className="text-gray-500 text-xs leading-relaxed">
                      문의 처리를 위해 성함, 연락처, 이메일 등의 개인정보를 수집합니다. 수집된 정보는 문의 답변 완료 후 관련 법령에 따라 일정 기간 보관 후 파기됩니다.
                    </p>
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={!agreed}
                  className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center transition-all ${
                    agreed 
                      ? 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Send size={20} className="mr-2" />
                  문의하기
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
